import React from "react";
import { FormattedMessage } from "react-intl";

import { USER_DOCUMENTATION_PATH } from "@/lib/constants";
import { DEFAULT_LANGUAGE, getLang } from "./IntlProviderWrapper";

import type { Language } from "./IntlProviderWrapper";

const DOCS_SUPPORTED_LANGUAGES: Language[] = ["en", "sl"];

type UserDocumentationLinkKey =
    | "plants"
    | "lines"
    | "line_groups"
    | "materials"
    | "people"
    | "batchop_locations"
    | "stock_locations"
    | "batchop_groups"
    | "latest_changes"
    | "home";

/**
 * Returns a relative documentation URL based on {@link UserDocumentationLinkKey|key} and `language` params.
 * - If the key is not found, it returns the documentation starting page.
 * - If the language is {@link DOCS_SUPPORTED_LANGUAGES|not supported}, it defaults to {@link DEFAULT_LANGUAGE}.
 */
const getLinkForUserDocumentation = (key: UserDocumentationLinkKey, language: Language): string => {
    const has_translation = key === "home" || key === "latest_changes";
    const is_language_supported = DOCS_SUPPORTED_LANGUAGES.includes(language);
    const normalized_language = has_translation && is_language_supported ? language : DEFAULT_LANGUAGE;
    const base_path = `${USER_DOCUMENTATION_PATH}/_pages/${normalized_language}`;
    const manage_path = `${base_path}/user_docs/05_manage.html`;

    const link_map: Record<UserDocumentationLinkKey, string> = {
        plants: `${manage_path}#plants`,
        line_groups: `${manage_path}#line-groups`,
        lines: `${manage_path}#lines`,
        materials: `${manage_path}#materials`,
        people: `${manage_path}#people`,
        batchop_locations: `${manage_path}#batchop-locations`,
        stock_locations: `${manage_path}#stock-locations`,
        batchop_groups: `${manage_path}#batchop-groups`,
        home: `${base_path}/user_docs/main.html`,
        latest_changes: `${base_path}/latest_changes/latest_changes.html`,
    };

    return link_map[key] ?? USER_DOCUMENTATION_PATH;
};

const DefaultDocumentationLinkContent = () => (
    <>
        <i className="far fa-question-circle" />{" "}
        <FormattedMessage id="Header.menu.user_documentation" defaultMessage="User documentation" />{" "}
        <small className="fas fa-external-link-alt" />
    </>
);

type UserDocumentationLinkProps = {
    link_key: UserDocumentationLinkKey;
    className?: string;
    children?: React.ReactNode;
};

const UserDocumentationLink = (props: UserDocumentationLinkProps) => {
    const { className, link_key, children } = props;
    const current_language = getLang();

    const href = React.useMemo(
        () => getLinkForUserDocumentation(link_key, current_language),
        [link_key, current_language]
    );

    return (
        <a className={className} href={href} target="_blank" rel="noopener noreferrer">
            {children || <DefaultDocumentationLinkContent />}
        </a>
    );
};

export default UserDocumentationLink;
export type { UserDocumentationLinkKey };
